import { useState, useMemo, useCallback, useEffect } from "react";
import { useNavigate, useParams } from "react-router";

import { ProductImageGallery } from "./ProductImageGallery";
import { ProductInfo } from "./ProductInfo";
import { ProductTabs } from "./ProductTabs";
import { ProductPageLoading } from "./ProductPageLoading";
import { Header } from "../common/Header";
import { DeliveryServices } from "../DeliveryServices";
import { NotFoundPage } from "../NotFoundPage";
import { SizeSelectionBottomSheet } from "./SizeSelectionBottomSheet";

import { useProduct, useNotificationHelpers } from "@nusrif/hooks";
import { useCart } from "@nusrif/contexts";
import { trackEvent, trackMetaPixel } from "@nusrif/services";

import {
  getLowestPriceVariant,
  isProductInStock,
  getProductAvailableOptions,
  isAuthenticated,
  isVariantInStock,
  getVariantStockLevel,
  isUserOnboarded,
} from "@nusrif/services";

import { formatCurrency } from "@nusrif/utils";
import config from "@nusrif/config";

import { Product } from "@nusrif/types";
import { BottomSheet } from "../ui/bottom-sheet";
import { OnboardingForm } from "../Auth/OnboardingForm";

export const ProductPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  // Listen to location changes for query params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const selectedSizeParam = params.get("selectedSize");
    const selectedVariantParam = params.get("selectedVariant");
    if (selectedSizeParam && selectedVariantParam) {
      setShowOnboardingForm(false);
      setSelectedSize(selectedSizeParam);
      setSelectedVariant(selectedVariantParam);
      handleAddToCart();
    }
    // eslint-disable-next-line
  }, [window.location.search]);

  if (!id) {
    return <NotFoundPage />;
  }

  const { product, isLoading, error } = useProduct(id);
  const { addItem, state } = useCart();
  const { showSuccess, showError } = useNotificationHelpers();

  const [selectedSize, setSelectedSize] = useState<string>("");
  const [selectedColor, setSelectedColor] = useState<string>("");
  const [selectedVariant, setSelectedVariant] = useState<string>("");
  const [showSizeSelection, setShowSizeSelection] = useState<boolean>(false);
  const [showOnboardingForm, setShowOnboardingForm] = useState<boolean>(false);

  // Track product view when product is loaded
  useEffect(() => {
    if (product && !isLoading && !error) {
      const lowestPriceVariant = getLowestPriceVariant(product);

      trackEvent({
        event: "product_view",
        properties: {
          product_id: product.id,
          product_name: product.name,
          product_slug: product.slug,
          price: lowestPriceVariant?.price || 0,
          currency:
            lowestPriceVariant?.currencyCode || config.region.currencyCode,
          category: product.facetValues?.map((fv) => fv.name).join(", ") || "",
          in_stock: isProductInStock(product),
          variant_count: product.variants.length,
          has_images: product.assets.length > 0,
        },
      });

      trackMetaPixel("ViewContent", {
        content_name: product.name,
        content_ids: [product.id],
        content_type: "product",
        value: lowestPriceVariant?.price || 0,
        currency: "INR",
      });
    }
  }, [product, isLoading, error]);

  // Memoize expensive computations
  const lowestPriceVariant = useMemo(
    () => (product ? getLowestPriceVariant(product) : null),
    [product]
  );

  // Get selected variant and its stock status
  const selectedVariantData = useMemo(() => {
    if (!product || !selectedVariant) return null;

    const variant = product.variants.find((v) => v.id === selectedVariant);
    if (!variant) return null;

    const stockLevel = getVariantStockLevel(variant);
    const isInStock = isVariantInStock(variant);

    let stockStatus: "in_stock" | "low_stock" | "out_of_stock";
    if (stockLevel <= variant.outOfStockThreshold) {
      stockStatus = "out_of_stock";
    } else if (stockLevel <= 5) {
      stockStatus = "low_stock";
    } else {
      stockStatus = "in_stock";
    }

    return {
      variant,
      stockLevel,
      isInStock,
      stockStatus,
    };
  }, [product, selectedVariant]);

  const isInStock = useMemo(
    () =>
      selectedVariantData?.isInStock ??
      (product ? isProductInStock(product) : false),
    [selectedVariantData, product]
  );

  const availableOptions = useMemo(
    () => (product ? getProductAvailableOptions(product) : []),
    [product]
  );

  // Extract size and color options
  const { sizeOptions, colorOptions } = useMemo(() => {
    if (!availableOptions.length) {
      return { sizeOptions: [], colorOptions: [] };
    }

    const sizeOpts =
      availableOptions
        .find((option) => option.name.toLowerCase().includes("size"))
        ?.options.map((opt) => opt.name) || [];

    const colorOpts =
      availableOptions
        .find((option) => option.name.toLowerCase().includes("color"))
        ?.options.map((opt) => ({ name: opt.name, value: opt.name })) || [];

    return { sizeOptions: sizeOpts, colorOptions: colorOpts };
  }, [availableOptions]);

  // Set default color selection on mount (no default size)
  useEffect(() => {
    if (colorOptions.length > 0 && !selectedColor) {
      setSelectedColor(colorOptions[0].name);
    }
  }, [colorOptions, selectedColor]);

  // Memoize product data for the interface
  const productData: Product | null = useMemo(() => {
    if (!product) return null;

    return {
      id: product.id,
      name: product.name,
      price: lowestPriceVariant?.price || 0,
      rating: 5, // Default rating since API doesn't provide it
      reviewCount: 0, // Default review count since API doesn't provide it
      availability: isInStock ? "Available in stock" : "Out of stock",
      sizes: sizeOptions,
      colors: colorOptions,
      images: [...product.assets.map((asset) => asset.preview)].filter(
        Boolean
      ) as string[],
      description: product.description,
      customFields: {
        material:
          product.customFields.material || "Material information not available",
        careInstruction:
          product.customFields.careInstruction ||
          "Care instructions not available",
        origin:
          product.customFields.origin || "Origin information not available",
        fit: product.customFields.fit || "Fit information not available",
        mrpPrice: product.customFields.mrpPrice || 0,
      },
      reviews: [],
      slug: product.slug,
      featuredAsset: product.featuredAsset,
      assets: product.assets,
      optionGroups: product.optionGroups,
      facetValues: product.facetValues,
      variants: product.variants,
    };
  }, [product, lowestPriceVariant, isInStock, sizeOptions, colorOptions]);

  const handleSizeChange = useCallback(
    (size: string) => {
      if (!product) return;

      const variant = product.variants.find((variant) =>
        variant.options.some((option) => option.name === size)
      );
      setSelectedVariant(variant?.id || "");
      setSelectedSize(size);

      // Track size selection
      trackEvent({
        event: "size_selected",
        properties: {
          product_id: product.id,
          product_name: product.name,
          size: size,
          variant_id: variant?.id || "",
          variant_price: variant?.price || 0,
          currency: variant?.currencyCode || config.region.currencyCode,
          in_stock: variant ? isVariantInStock(variant) : false,
          stock_level: variant ? getVariantStockLevel(variant) : 0,
        },
      });
    },
    [product]
  );

  const handleSizeSelectFromBottomSheet = useCallback(
    (size: string) => {
      handleSizeChange(size);
    },
    [handleSizeChange]
  );

  const handleAddToCartFromBottomSheet = useCallback(async () => {
    if (!selectedVariantData?.variant) return;

    if (!isAuthenticated()) {
      console.error("User is not authenticated. Cannot add item to cart.");
      navigate("/signin?returnUrl=/product/" + id);
      return;
    }

    if (isAuthenticated() && !isUserOnboarded()) {
      console.error("User is authenticated. But not onboarded.");
      const buildActionParams = `selectedVariant=${selectedVariant}&quantity=1&selectedSize=${selectedSize}&selectedColor=${selectedColor}`;
      navigate(`/product/${id}?returnUrl=/product/${id}&${buildActionParams}`, {
        replace: true,
      });
      setShowOnboardingForm(true);
      return;
    }

    try {
      console.log("Adding product variant:", selectedVariant);
      const response = await addItem(selectedVariant, 1);

      if (!response.success) {
        throw new Error(response.error);
      }

      showSuccess("Added to Cart!", product?.name);
    } catch (error) {
      console.error("Failed to add item to cart:", error);
      showError("Failed to add item", "Please try again");
    }
  }, [
    selectedVariantData,
    selectedVariant,
    addItem,
    showSuccess,
    showError,
    product?.name,
  ]);

  const handleColorChange = useCallback((color: string) => {
    setSelectedColor(color);
  }, []);

  const handleAddToCart = useCallback(async () => {
    // If no size is selected, show size selection bottom sheet
    if (!selectedSize) {
      setShowSizeSelection(true);
      return;
    }

    if (!selectedVariantData?.variant) return;

    if (!isAuthenticated()) {
      console.error("User is not authenticated. Cannot add item to cart.");
      navigate("/signin?returnUrl=/product/" + id);
      return;
    }

    console.log("isUserOnboarded:", isUserOnboarded());

    if (isAuthenticated() && !isUserOnboarded()) {
      setShowOnboardingForm(true);
      return;
    }

    try {
      console.log("Adding product variant:", selectedVariant);
      const response = await addItem(selectedVariant, 1);

      if (!response.success) {
        throw new Error(response.error);
      }

      showSuccess("Added to Cart!", product?.name);
    } catch (error) {
      console.error("Failed to add item to cart:", error);
      showError("Failed to add item", "Please try again");
    }
  }, [
    selectedSize,
    selectedVariantData,
    selectedVariant,
    addItem,
    showSuccess,
    showError,
    product?.name,
  ]);

  const formattedPrice = useMemo(
    () =>
      selectedVariantData?.variant
        ? formatCurrency(
            selectedVariantData.variant.priceWithTax,
            selectedVariantData.variant.currencyCode
          )
        : lowestPriceVariant
          ? formatCurrency(
              lowestPriceVariant.priceWithTax,
              lowestPriceVariant.currencyCode
            )
          : formatCurrency(0, config.region.currencyCode),
    [selectedVariantData, lowestPriceVariant]
  );

  const priceNumbers = useMemo(() => {
    const currentPrice = selectedVariantData?.variant
      ? selectedVariantData.variant.priceWithTax
      : lowestPriceVariant
        ? lowestPriceVariant.priceWithTax
        : 0;

    const currencyCode = selectedVariantData?.variant
      ? selectedVariantData.variant.currencyCode
      : lowestPriceVariant?.currencyCode || config.region.currencyCode;

    const mrp = product?.customFields?.mrpPrice ?? 0;

    const discount =
      mrp > 0 && currentPrice > 0 && mrp > currentPrice
        ? Math.round(((mrp - currentPrice) / mrp) * 100)
        : 0;

    return { currentPrice, mrp, discount, currencyCode };
  }, [selectedVariantData, lowestPriceVariant, product]);

  const formattedMrp = useMemo(
    () => formatCurrency(priceNumbers.mrp, priceNumbers.currencyCode),
    [priceNumbers]
  );

  // Error state
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Header showBackButton={true} />
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Product Not Found
          </h2>
          <p className="text-gray-600">
            The product you're looking for doesn't exist.
          </p>
        </div>
      </div>
    );
  }

  // Loading state
  if (isLoading || !product || !productData) {
    return <ProductPageLoading />;
  }

  return (
    <div className="min-h-screen w-full bg-white">
      {/* Fixed header */}
      <Header
        showBackButton={true}
        showShareButton={true}
        product={productData}
      />

      {/* Scrollable content */}
      <div className="pt-16 pb-24">
        <ProductImageGallery
          images={productData.images}
          productId={product.id}
        />

        <div className="px-5 space-y-6">
          <ProductInfo
            product={productData}
            selectedSize={selectedSize}
            selectedColor={selectedColor}
            onSizeChange={handleSizeChange}
            onColorChange={handleColorChange}
          />

          <DeliveryServices />

          <ProductTabs
            description={product.description}
            details={productData.customFields}
            reviews={productData.reviews}
          />
        </div>
      </div>

      {/* Fixed bottom section */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-5 py-4 z-50">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <p className="text-xs text-gray-600">Total Price</p>
              {priceNumbers.discount > 0 && (
                <span className="text-xs text-green-900 font-medium">
                  {priceNumbers.discount}% off
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-gray-500 line-through">{formattedMrp}</span>
              <span className="text-2xl font-bold text-black">
                {formattedPrice}
              </span>
            </div>
          </div>
          <button
            className={`px-8 py-4 rounded-full font-semibold text-lg transition-colors duration-200 flex items-center gap-2 ${
              !selectedSize
                ? "bg-primary-blue text-white hover:bg-primary-blue/90"
                : isInStock && selectedVariantData?.variant
                  ? "bg-primary-blue text-white hover:bg-primary-blue/90"
                  : "bg-gray-300 text-gray-500 cursor-not-allowed"
            }`}
            onClick={
              selectedSize ? handleAddToCart : () => setShowSizeSelection(true)
            }
            disabled={state.isSyncing}
          >
            {state.isSyncing
              ? "Adding..."
              : !selectedSize
                ? "Select Size"
                : isInStock
                  ? selectedVariantData?.stockStatus === "low_stock"
                    ? `Add to cart (${selectedVariantData.stockLevel} left)`
                    : "Add to cart"
                  : "Out of stock"}
          </button>
        </div>
      </div>

      {/* Size Selection Bottom Sheet */}
      {productData && (
        <SizeSelectionBottomSheet
          isOpen={showSizeSelection}
          onClose={() => setShowSizeSelection(false)}
          product={productData}
          selectedSize={selectedSize}
          onSizeSelect={handleSizeSelectFromBottomSheet}
          onAddToCart={handleAddToCartFromBottomSheet}
        />
      )}

      <BottomSheet
        isOpen={showOnboardingForm}
        onClose={() => {
          setShowOnboardingForm(false);
        }}
        title="Welcome! Let's set up your profile"
        disableCloseButton={true}
      >
        <OnboardingForm />
      </BottomSheet>
    </div>
  );
};
